import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserRound, UserPlus, Check, ChevronDown, ArrowLeft } from "lucide-react";
import { fetchDependents, createDependent } from "../apis/api.dependents";
import { queryDependentsKey } from "../apis/queryKeys";
import { useAuth } from "../lib/AuthContext";
import useActingPatient, { SELF } from "../hooks/useActingPatient";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { cn } from "../utils/utils";
import Button from "./common/Button";
import Spinner from "./common/Spinner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "./common/Dialog";
import DependentForm from "./DependentForm";
import customToast from "./common/Toaster";

// Selector "Operar con…": el titular elige operar como él mismo o como un
// familiar, o agrega uno nuevo desde el mismo diálogo. Persiste el paciente
// activo (useActingPatient), que PatientBooking usa como sujeto por defecto.
export default function ActingPatientSelector({ className }) {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const guardianId = user?.id;
  const queryClient = useQueryClient();
  const { acting, setActing, isSelf } = useActingPatient();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | "add"

  const { data: dependents = [], isLoading } = useQuery({
    queryKey: queryDependentsKey(guardianId),
    queryFn: () => fetchDependents(guardianId),
    enabled: !!guardianId && open,
  });

  const createMutation = useMutation({
    mutationFn: (values) => createDependent(guardianId, values),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: queryDependentsKey(guardianId) });
      customToast(t("app.dependents.created", "Familiar agregado"), "success");
      setActing({
        id: created.id,
        name: `${created.name || ""} ${created.last_name || ""}`.trim(),
      });
      setView("list");
      setOpen(false);
    },
    onError: (err) => {
      customToast(
        err.message || t("app.dependents.saveError", "Error al guardar el familiar"),
        "danger"
      );
    },
  });

  const selfName =
    `${user?.name || ""} ${user?.last_name || ""}`.trim() ||
    t("app.acting.myself", "Yo");
  const currentLabel = isSelf ? selfName : acting.name;

  const choose = (option) => {
    setActing(option);
    setOpen(false);
  };

  const closeDialog = () => {
    setOpen(false);
    setView("list");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setView("list");
          setOpen(true);
        }}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50",
          className
        )}
      >
        <span className="flex items-center gap-2 min-w-0">
          <UserRound className="h-4 w-4 shrink-0 text-gray-500" />
          <span className="truncate">
            <span className="text-gray-500">
              {t("app.acting.operatingWith", "Operar con")}:{" "}
            </span>
            <span className="font-medium">{currentLabel}</span>
          </span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
        <DialogContent className="w-[92vw] max-w-md p-0 flex flex-col max-h-[85vh] overflow-hidden">
          {view === "list" ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  {t("app.acting.title", "¿Con quién querés operar?")}
                </DialogTitle>
              </DialogHeader>

              <DialogBody className="space-y-1">
                {isLoading ? (
                  <Spinner className="w-8 h-8 mx-auto my-6" />
                ) : (
                  <>
                    <OptionRow
                      label={selfName}
                      caption={t("app.acting.myselfCaption", "Titular de la cuenta")}
                      selected={isSelf}
                      onClick={() => choose({ id: SELF })}
                    />
                    {dependents.map((dep) => (
                      <OptionRow
                        key={dep.id}
                        label={`${dep.name} ${dep.last_name}`}
                        caption={dep.dni ? `${t("app.dependents.dni", "DNI")}: ${dep.dni}` : null}
                        selected={acting.id === dep.id}
                        onClick={() =>
                          choose({
                            id: dep.id,
                            name: `${dep.name || ""} ${dep.last_name || ""}`.trim(),
                          })
                        }
                      />
                    ))}
                  </>
                )}
              </DialogBody>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setView("add")}
                >
                  <UserPlus className="h-4 w-4 mr-2 inline" />
                  {t("app.dependents.add", "Agregar familiar")}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setView("list")}
                    className="text-gray-500 hover:text-gray-800"
                    aria-label={t("common.back", "Volver")}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  {t("app.dependents.addTitle", "Agregar familiar")}
                </DialogTitle>
                <p className="text-xs text-gray-500">
                  {t(
                    "app.acting.addAndSelect",
                    "Se agregará y quedará seleccionado para operar."
                  )}
                </p>
              </DialogHeader>
              <DependentForm
                onSubmit={(values) => createMutation.mutate(values)}
                onCancel={() => setView("list")}
                submitting={createMutation.isPending}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function OptionRow({ label, caption, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-gray-50",
        selected && "bg-gray-50"
      )}
    >
      <span className="min-w-0">
        <span className="block font-medium text-gray-900 truncate">{label}</span>
        {caption && <span className="block text-xs text-gray-500 truncate">{caption}</span>}
      </span>
      {selected && <Check className="h-4 w-4 shrink-0 text-green-600" />}
    </button>
  );
}
